import { HStack, Spinner, VStack, Text, Box, Flex } from '@chakra-ui/react';
import { SearchFilterInput } from '../../SearchFilterInput';
import { useCallback, useEffect, useState } from 'react';
import { BrandCard } from './BrandCard';
import { useTranslation } from 'react-i18next';
import { customersApi, BrandsDto } from '@texas/api/endpoints/customersApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useActiveSession } from '@texas/hooks/useSession';
import { BaseWidgetProps } from '../widgets';
import { WidgetWrapper } from '../shared/components/WidgetWrapper';
import { BaseWidgetOptions, WidgetType } from '../shared/types';
import { NoData } from '@texas/components/shared/NoData';
import { customerEvents } from '@texas/components/customer/events';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';

interface Props extends BaseWidgetProps {
  options?: BaseWidgetOptions;
}

export function BrandsWidget({ color, customTitle, options, widgetId }: Props) {
  const {
    data: brands,
    refetch,
    loading,
  } = useApiResource(customersApi.filterCustomers);
  const [filter, setFilter] = useState('');
  const { currentUser } = useActiveSession();

  useEffect(() => {
    refetch({
      assignedTo: currentUser.id,
      customerName: filter,
    });
  }, [filter, refetch, currentUser.id]);

  const handleRefetch = useCallback(() => {
    refetch({ assignedTo: currentUser.id, customerName: filter });
  }, [refetch, currentUser.id, filter]);

  useSignalSubscriptionEvents({
    triggerOnEvent: handleRefetch,
    signalEvents: [customerEvents.brandCreated, customerEvents.brandUpdated],
  });

  const { t } = useTranslation();

  return (
    <WidgetWrapper
      widgetId={widgetId}
      loading={loading}
      settings={options?.componentSettings}
      customColor={color}
      type={WidgetType.Brands}
      style="borderless"
      header={customTitle ?? t('widgets.brands.yourBrands')}
    >
      <VStack align="start" pos="relative" height="100%">
        <HStack>
          <SearchFilterInput
            style="unstyled"
            value={filter}
            placeholder={t('widgets.brands.filterBrands')}
            onChange={(value) => setFilter(value)}
          />

          {loading && (
            <Box>
              <Spinner />
            </Box>
          )}
        </HStack>

        {brands?.length === 0 && !loading && (
          <NoData showIcon={true} label={t('widgets.brands.noBrandsFound')} />
        )}
        <Brands brands={brands} isLoading={loading} />
      </VStack>
    </WidgetWrapper>
  );
}

function Brands({
  brands,
  isLoading,
}: {
  brands: BrandsDto[] | null;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  if (!brands && !isLoading) {
    return (
      <Text margin="auto !important" alignSelf="center">
        {t('widgets.brands.filterAbove')}
      </Text>
    );
  }
  return <BrandsInnerComponent brands={brands} />;
}

function BrandsInnerComponent({ brands }: { brands: BrandsDto[] | null }) {
  return (
    <Flex gap={2} flexWrap="wrap">
      {brands?.map((b) => (
        <BrandCard
          key={b.id}
          id={b.id}
          name={b.name}
          nrOfArticles={b.nrOfArticles}
          logoIdentifier={b.logoIdentifier}
          currentUserRoles={b.currentUserRoles}
          currentUserCustomRoles={b.currentUserCustomRoles}
        />
      ))}
    </Flex>
  );
}
